import { gelNumberOf } from '@/utils/money';
import type {
    CalculateDistributionResponse,
    CalculatorDirection,
    CommitDividendDeclarationResponse,
    CreateShareholderResponse,
    DistributionQuote,
    DividendDeclaration,
    DividendDeclarationSummary,
    DividendLine,
    DividendTotals,
    DividendsSummary,
    DraftEntryPreview,
    GetDividendDeclarationResponse,
    GetDividendDeclarationsResponse,
    GetShareholdersResponse,
    JournalEntryStatus,
    LocaleCode,
    PreviewDividendDeclarationResponse,
    Shareholder,
    ShareholderResidency,
    ShareholderType,
    TaxRegime,
    UpdateShareholderResponse,
} from '@/api/models';
import { regimeFromWire } from '../settings/taxSettingsMapping';
import {
    decimalGelOf,
    mapJournalEntry,
    mapLine,
    mapValidationState,
    type LineWire,
} from '../journal/journalMapping';

/**
 * Wire ⇄ domain mapping for the Profit & Dividends register (bos-api, BOS-112).
 *
 * **This is the only file that knows the server's field names.** Everything
 * downstream — the service, the query hooks, the page hook, the shareholder
 * table, the tie-out bar, the extraction calculator, the declare dialog, the
 * declaration history — sees the domain shapes in `@/api/models/Dividends`. If the
 * wire disagrees with what is assumed here, this file is the whole of the fix.
 * Same seam, and the same reason, as `accountsMapping`, `journalMapping`,
 * `statementsMapping`, `vatMapping`, `periodsMapping`, `fixedAssetsMapping` and
 * `salariesMapping`. This is the eighth such integration, and the last accounting
 * domain: nothing in this client is mock-backed after it.
 *
 * Measured against `/openapi/v1.json` and live responses on **2026-08-12**
 * (7 paths, 9 operations), and against the API source at `bb00e98`.
 *
 * ## Systemic divergences from the pre-integration contract
 *
 * | client contract | wire |
 * | --- | --- |
 * | route `/dividends/*` | **`api/v1/ledger/dividends/*`** |
 * | `422` on validation | **`400`**, always — this API has no 422 |
 * | money as decimal **strings** (`"100000.00"`) | JSON **numbers** (`gross: 100000`) plus the authoritative `grossTetri: long` |
 * | reads open to `BranchAdmin` | **`OrgAdmin` on every route, reads included** — see `dividendsAccess` |
 * | `DividendDeclaration.id` / `DividendDeclarationSummary.id` | **gone** — a declaration *is* its entry, so `journalEntryId` is the id |
 * | `DividendDeclaration.status` (`PREVIEW`/`COMMITTED`) | **gone** — replaced by `entryStatus`, the *entry's* status |
 * | client posts the entry, then commit verifies it account by account | **gone** — commit **builds** the DRAFT server-side and returns it |
 * | `declarationId` minted per preview and carried into the commit | **gone** — commit is idempotent on `(date, charge)`; there is no client id |
 * | `CommitDividendDeclarationRequest.journalEntryId` | **gone** — preview, calculate and commit all take the same `DividendDistributionRequest` |
 * | `summary.*AccountId` non-null | **nullable** — null when the chart has no account with that role |
 * | `summary.declaredUnpaidDividends` read as **gross** | it is the **net** — see the tie-out note below |
 * | `Shareholder.withholdingTaxBps` non-null | **nullable override**, with `effectiveWithholdingTaxBps` alongside |
 * | *(absent)* | `regime` on a declaration and a quote, `identifier` on a line, `distributableProfit` on a quote, `entryNumberFormatted`, `DELETE shareholders/{id}` |
 *
 * ## Five properties of the wire this mapping preserves rather than "corrects"
 *
 * 1. **The `*Tetri` integer is authoritative; the decimal companion is an echo.**
 *    The wire ships `gross: 85000` as a JSON *number* next to `grossTetri:
 *    8500000`. Every decimal string downstream is re-derived here from the tetri
 *    via {@link decimalGelOf}, so the two can never disagree on screen.
 *
 * 2. **Preview drafts and committed drafts are different shapes, and stay so.**
 *    A preview returns `DraftEntryPreview` (no id, no etag — nothing to open); a
 *    commit returns a full `JournalEntryResponse` (real id, etag, `status: DRAFT`).
 *    {@link mapDraftEntryPreview} and {@link mapJournalEntry} are deliberately
 *    separate, so an unsaved preview cannot reach the journal entry form.
 *
 * 3. **A committed declaration has posted nothing.** `entryStatus` comes through
 *    untouched, `DRAFT` and all. Synthesising a "committed means charged" state
 *    here would make the history claim ledger positions that do not exist until an
 *    accountant posts the draft.
 *
 * 4. **Totals are the server's, not a sum of the lines**, and `net` on a line is
 *    the server's too — derived from that line's own rounded components, which is
 *    what makes the per-shareholder amounts sum to the entry totals exactly.
 *
 * 5. **The two withholding rates stay two fields.** A stored `null` means "take
 *    the entity default", not 0%. Collapsing them would turn a holder who has
 *    never had a rate decided into one whose treaty rate was negotiated to zero.
 *
 * ## Measured, and not in the spec
 *
 * - **`declaredUnpaidDividends` is the net, not the gross.** It sums the
 *   declaration lines' `net`, which is exactly what the entry credited to
 *   dividends payable. The pre-integration tie-out subtracted the withholding from
 *   it before comparing, because the mock carried the gross there; doing that
 *   against this wire would report a permanent difference of exactly the
 *   withholding, in the opposite direction.
 * - **The preview draft's `validationState` already includes the period gate**,
 *   as payroll's does — `DividendDeclarationService` runs the posting guard for
 *   the declaration date and folds its rejection into the validation errors. So
 *   {@link mapDraftEntryPreview} passes `dateInClosedPeriod: false` and lets the
 *   server's own errors speak; adding a second PERIOD_CLOSED here would show the
 *   user one reason twice.
 * - **An over-limit distribution is priced, not refused** — by `calculate` and by
 *   `preview`, which return `exceedsDistributable`, the exact `shortfall` and a
 *   null `draft`. Only `commit` refuses, with
 *   `EXCEEDS_DISTRIBUTABLE_PROFIT`, because only a commit creates something
 *   postable.
 * - **`NO_RATES_IN_FORCE` is a date-shaped refusal, not a failure.** It means no
 *   `TaxRateSet` is in force on the declaration's date. The *register* has no such
 *   refusal: it lists shareholders with a null `effectiveWithholdingTaxBps`.
 * - **Dates are `DateOnly`** (`"2026-08-12"`) and instants are `DateTime`; both
 *   arrive as plain strings and are passed through, never parsed.
 */

// ── wire shapes ─────────────────────────────────────────────────────────────

type LocaleMapWire = Record<string, string> | null | undefined;

export type ShareholderWire = {
    id?: string;
    name?: LocaleMapWire;
    identifier?: string;
    type?: string;
    residency?: string;
    holdingBps?: number;
    withholdingTaxBps?: number | null;
    effectiveWithholdingTaxBps?: number | null;
    withholdingNote?: string;
    citExempt?: boolean;
    createdAt?: string;
    updatedAt?: string;
};

export type DividendLineWire = {
    shareholderId?: string;
    shareholderName?: LocaleMapWire;
    identifier?: string;
    holdingBps?: number;
    withholdingTaxBps?: number;
    citExempt?: boolean;
    grossTetri?: number;
    withholdingTaxTetri?: number;
    netTetri?: number;
    citTetri?: number;
    isManualAllocation?: boolean;
};

export type DividendTotalsWire = {
    chargeTetri?: number;
    grossDividendTetri?: number;
    citTetri?: number;
    withholdingTaxTetri?: number;
    netTetri?: number;
    effectiveRateBps?: number;
};

export type DividendDeclarationWire = {
    date?: string;
    citRateBps?: number;
    regime?: string;
    lines?: unknown[];
    totals?: unknown;
    journalEntryId?: string | null;
    entryStatus?: string | null;
    entryNumberFormatted?: string | null;
    committedAt?: string | null;
    dueDate?: string;
};

export type DividendDeclarationSummaryWire = {
    date?: string;
    shareholderCount?: number;
    grossDividendTetri?: number;
    chargeTetri?: number;
    citTetri?: number;
    netTetri?: number;
    journalEntryId?: string;
    entryStatus?: string;
    entryNumberFormatted?: string | null;
    committedAt?: string;
    dueDate?: string;
};

// ── primitives ──────────────────────────────────────────────────────────────

const asRecord = (raw: unknown): Record<string, unknown> =>
    typeof raw === 'object' && raw !== null
        ? (raw as Record<string, unknown>)
        : {};

const asArray = (raw: unknown): unknown[] => (Array.isArray(raw) ? raw : []);

const asString = (raw: unknown): string => (typeof raw === 'string' ? raw : '');

const asNumber = (raw: unknown): number =>
    typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;

/** A nullable basis-point field, where null carries meaning. See the header. */
const asNullableBps = (raw: unknown): number | null =>
    typeof raw === 'number' && Number.isFinite(raw) ? raw : null;

/**
 * The wire's `{ en, ka }` map, kept as a map — names are not resolved
 * server-side, so the display layer picks the locale.
 */
const mapLocaleMap = (
    raw: LocaleMapWire,
): Partial<Record<LocaleCode, string>> => {
    const out: Partial<Record<LocaleCode, string>> = {};
    if (!raw) return out;
    for (const [key, value] of Object.entries(raw)) {
        if (key === 'en' || key === 'ka') out[key] = value;
    }
    return out;
};

/**
 * An unrecognised type maps to `INDIVIDUAL` rather than dropping the holder — a
 * row with the wrong badge is recoverable, a shareholder that vanishes from the
 * register is not. Same reasoning for residency.
 */
const mapType = (raw: string | undefined): ShareholderType =>
    raw === 'COMPANY' ? 'COMPANY' : 'INDIVIDUAL';

const mapResidency = (raw: string | undefined): ShareholderResidency =>
    raw === 'NON_RESIDENT' ? 'NON_RESIDENT' : 'RESIDENT';

const DIRECTIONS: readonly CalculatorDirection[] = [
    'BY_CHARGE',
    'BY_GROSS',
    'BY_NET',
];

/** The direction is echoed back; an unreadable one falls back to what was asked. */
const mapDirection = (raw: unknown): CalculatorDirection =>
    DIRECTIONS.find((direction) => direction === raw) ?? 'BY_CHARGE';

/**
 * The regime, through the same name map the settings screen uses — the wire says
 * `StandardLlc`, this client says `STANDARD`. An unreadable regime falls back to
 * `STANDARD` rather than failing the read: the rates on the declaration are the
 * server's own and are unaffected, so the only thing at stake is a label.
 */
const mapRegime = (raw: unknown): TaxRegime =>
    (typeof raw === 'string' ? regimeFromWire(raw) : null) ?? 'STANDARD';

const ENTRY_STATUSES: readonly JournalEntryStatus[] = [
    'DRAFT',
    'POSTED',
    'REVERSED',
];

/**
 * The **entry's** status, not the declaration's — a declaration has no status of
 * its own.
 *
 * Null (a preview) stays null. An unrecognised value also becomes null rather
 * than defaulting to `POSTED`: "we do not know what state this entry is in" is
 * survivable, "this draft has been posted" is a claim about money.
 */
const mapEntryStatus = (
    raw: string | null | undefined,
): JournalEntryStatus | null =>
    ENTRY_STATUSES.find((status) => status === raw) ?? null;

/**
 * The three account ids stay **null** when the server sends null.
 *
 * Null means the chart carries no account tagged for that dividends role, so
 * there is no ledger figure to compare the register's against. Substituting an
 * empty string would let the comparison run against a lookup that always misses
 * and report a difference equal to the whole register.
 */
const mapAccountId = (raw: unknown): string | null =>
    typeof raw === 'string' && raw !== '' ? raw : null;

// ── the register ────────────────────────────────────────────────────────────

export const mapShareholder = (raw: unknown): Shareholder => {
    const wire = asRecord(raw) as ShareholderWire;

    return {
        id: wire.id ?? '',
        name: mapLocaleMap(wire.name),
        identifier: wire.identifier ?? '',
        type: mapType(wire.type),
        residency: mapResidency(wire.residency),
        holdingBps: asNumber(wire.holdingBps),
        withholdingTaxBps: asNullableBps(wire.withholdingTaxBps),
        effectiveWithholdingTaxBps: asNullableBps(
            wire.effectiveWithholdingTaxBps,
        ),
        withholdingNote: wire.withholdingNote ?? '',
        citExempt: wire.citExempt ?? false,
        createdAt: wire.createdAt,
        updatedAt: wire.updatedAt,
    };
};

const mapSummary = (raw: unknown): DividendsSummary => {
    const wire = asRecord(raw);
    const retainedEarningsTetri = asNumber(wire.retainedEarningsTetri);
    const resultForPeriodTetri = asNumber(wire.resultForPeriodTetri);
    const declaredUnpaidDividendsTetri = asNumber(
        wire.declaredUnpaidDividendsTetri,
    );
    const distributableProfitTetri = asNumber(wire.distributableProfitTetri);
    const corporateIncomeTaxPayableTetri = asNumber(
        wire.corporateIncomeTaxPayableTetri,
    );
    const withholdingTaxPayableTetri = asNumber(
        wire.withholdingTaxPayableTetri,
    );

    return {
        retainedEarnings: decimalGelOf(retainedEarningsTetri),
        retainedEarningsTetri,
        resultForPeriod: decimalGelOf(resultForPeriodTetri),
        resultForPeriodTetri,
        declaredUnpaidDividends: decimalGelOf(declaredUnpaidDividendsTetri),
        declaredUnpaidDividendsTetri,
        distributableProfit: decimalGelOf(distributableProfitTetri),
        distributableProfitTetri,
        corporateIncomeTaxPayable: decimalGelOf(corporateIncomeTaxPayableTetri),
        corporateIncomeTaxPayableTetri,
        withholdingTaxPayable: decimalGelOf(withholdingTaxPayableTetri),
        withholdingTaxPayableTetri,
        shareholderCount: asNumber(wire.shareholderCount),
        totalHoldingBps: asNumber(wire.totalHoldingBps),
        dividendsPayableAccountId: mapAccountId(wire.dividendsPayableAccountId),
        corporateIncomeTaxPayableAccountId: mapAccountId(
            wire.corporateIncomeTaxPayableAccountId,
        ),
        withholdingTaxPayableAccountId: mapAccountId(
            wire.withholdingTaxPayableAccountId,
        ),
    };
};

export const mapShareholderList = (raw: unknown): GetShareholdersResponse => {
    const wire = asRecord(raw);
    return {
        items: asArray(wire.items).map(mapShareholder),
        summary: mapSummary(wire.summary),
    };
};

export const mapCreateShareholder = (
    raw: unknown,
): CreateShareholderResponse => {
    const wire = asRecord(raw);
    return {
        shareholder: mapShareholder(wire.shareholder),
        alreadyExisted: wire.alreadyExisted === true,
    };
};

export const mapUpdateShareholder = (
    raw: unknown,
): UpdateShareholderResponse => ({
    shareholder: mapShareholder(asRecord(raw).shareholder),
});

// ── declarations ────────────────────────────────────────────────────────────

const mapDividendLine = (raw: unknown): DividendLine => {
    const wire = asRecord(raw) as DividendLineWire;
    const grossTetri = asNumber(wire.grossTetri);
    const withholdingTaxTetri = asNumber(wire.withholdingTaxTetri);
    const netTetri = asNumber(wire.netTetri);
    const citTetri = asNumber(wire.citTetri);

    return {
        shareholderId: wire.shareholderId ?? '',
        shareholderName: mapLocaleMap(wire.shareholderName),
        identifier: wire.identifier ?? '',
        holdingBps: asNumber(wire.holdingBps),
        withholdingTaxBps: asNumber(wire.withholdingTaxBps),
        citExempt: wire.citExempt ?? false,
        gross: decimalGelOf(grossTetri),
        grossTetri,
        withholdingTax: decimalGelOf(withholdingTaxTetri),
        withholdingTaxTetri,
        // The server's, from this line's own rounded components. Never recomputed
        // here — a second opinion about net is how a preview and the entry behind
        // it end up a tetri apart.
        net: decimalGelOf(netTetri),
        netTetri,
        cit: decimalGelOf(citTetri),
        citTetri,
        isManualAllocation: wire.isManualAllocation ?? false,
    };
};

const mapTotals = (raw: unknown): DividendTotals => {
    const wire = asRecord(raw) as DividendTotalsWire;
    const chargeTetri = asNumber(wire.chargeTetri);
    const grossDividendTetri = asNumber(wire.grossDividendTetri);
    const citTetri = asNumber(wire.citTetri);
    const withholdingTaxTetri = asNumber(wire.withholdingTaxTetri);
    const netTetri = asNumber(wire.netTetri);

    return {
        charge: decimalGelOf(chargeTetri),
        chargeTetri,
        grossDividend: decimalGelOf(grossDividendTetri),
        grossDividendTetri,
        cit: decimalGelOf(citTetri),
        citTetri,
        withholdingTax: decimalGelOf(withholdingTaxTetri),
        withholdingTaxTetri,
        net: decimalGelOf(netTetri),
        netTetri,
        effectiveRateBps: asNumber(wire.effectiveRateBps),
    };
};

export const mapDeclaration = (raw: unknown): DividendDeclaration => {
    const wire = asRecord(raw) as DividendDeclarationWire;
    return {
        date: wire.date ?? '',
        citRateBps: asNumber(wire.citRateBps),
        regime: mapRegime(wire.regime),
        lines: asArray(wire.lines).map(mapDividendLine),
        // The server's totals, not a sum of the lines — see the header note.
        totals: mapTotals(wire.totals),
        journalEntryId: wire.journalEntryId ?? null,
        entryStatus: mapEntryStatus(wire.entryStatus),
        entryNumberFormatted: wire.entryNumberFormatted ?? null,
        committedAt: wire.committedAt ?? null,
        dueDate: wire.dueDate ?? '',
    };
};

/**
 * A declaration in the history. `journalEntryId` is its identity — the id `GET
 * declarations/{id}` takes — and a committed declaration always has one.
 */
export const mapDeclarationSummary = (
    raw: unknown,
): DividendDeclarationSummary => {
    const wire = asRecord(raw) as DividendDeclarationSummaryWire;
    const grossDividendTetri = asNumber(wire.grossDividendTetri);
    const chargeTetri = asNumber(wire.chargeTetri);
    const citTetri = asNumber(wire.citTetri);
    const netTetri = asNumber(wire.netTetri);

    return {
        date: wire.date ?? '',
        shareholderCount: asNumber(wire.shareholderCount),
        grossDividend: decimalGelOf(grossDividendTetri),
        grossDividendTetri,
        charge: decimalGelOf(chargeTetri),
        chargeTetri,
        cit: decimalGelOf(citTetri),
        citTetri,
        net: decimalGelOf(netTetri),
        netTetri,
        journalEntryId: wire.journalEntryId ?? '',
        // A committed declaration's entry always exists, so an unreadable status
        // falls back to DRAFT — the state that claims the least.
        entryStatus: mapEntryStatus(wire.entryStatus) ?? 'DRAFT',
        entryNumberFormatted: wire.entryNumberFormatted ?? null,
        committedAt: wire.committedAt ?? '',
        dueDate: wire.dueDate ?? '',
    };
};

export const mapDeclarationList = (
    raw: unknown,
): GetDividendDeclarationsResponse => ({
    items: asArray(asRecord(raw).items).map(mapDeclarationSummary),
});

export const mapDeclarationDetail = (
    raw: unknown,
): GetDividendDeclarationResponse => ({
    declaration: mapDeclaration(asRecord(raw).declaration),
});

// ── quotes ──────────────────────────────────────────────────────────────────

export const mapQuote = (raw: unknown): DistributionQuote => {
    const wire = asRecord(raw);
    const shortfallTetri = asNumber(wire.shortfallTetri);
    const distributableProfitTetri = asNumber(wire.distributableProfitTetri);

    return {
        direction: mapDirection(wire.direction),
        citRateBps: asNumber(wire.citRateBps),
        regime: mapRegime(wire.regime),
        lines: asArray(wire.lines).map(mapDividendLine),
        totals: mapTotals(wire.totals),
        exceedsDistributable: wire.exceedsDistributable === true,
        shortfall: decimalGelOf(shortfallTetri),
        shortfallTetri,
        distributableProfit: decimalGelOf(distributableProfitTetri),
        distributableProfitTetri,
    };
};

export const mapCalculateDistribution = (
    raw: unknown,
): CalculateDistributionResponse => ({
    quote: mapQuote(asRecord(raw).quote),
});

// ── drafts ──────────────────────────────────────────────────────────────────

/**
 * A preview's unsaved draft — no id, no etag, nothing to open. Kept structurally
 * distinct from {@link mapJournalEntry}: only a commit produces an entry the
 * journal form can take.
 *
 * `dateInClosedPeriod` is `false` because the server has **already** run the
 * period gate and folded any rejection into `validationState.errors` (measured;
 * see the header note).
 */
export const mapDraftEntryPreview = (raw: unknown): DraftEntryPreview => {
    const wire = asRecord(raw);
    const totalDebitTetri = asNumber(wire.totalDebitTetri);
    const totalCreditTetri = asNumber(wire.totalCreditTetri);
    // Through the journal's own line mapper: the wire shape is
    // `JournalLineResponse`, and it marks every field optional.
    const lines = asArray(wire.lines).map((line) => mapLine(line as LineWire));

    return {
        date: asString(wire.date),
        description: asString(wire.description),
        lines,
        totalDebit: decimalGelOf(totalDebitTetri),
        totalDebitTetri,
        totalCredit: decimalGelOf(totalCreditTetri),
        totalCreditTetri,
        validationState: mapValidationState(
            wire.validationState as Parameters<typeof mapValidationState>[0],
            lines,
            false,
        ),
    };
};

export const mapPreviewDeclaration = (
    raw: unknown,
): PreviewDividendDeclarationResponse => {
    const wire = asRecord(raw);
    return {
        declaration: mapDeclaration(wire.declaration),
        quote: mapQuote(wire.quote),
        // Null when the distribution is nil or cannot be funded. The preview
        // still succeeds and still explains every shareholder; only the commit
        // refuses.
        draft: wire.draft ? mapDraftEntryPreview(wire.draft) : null,
    };
};

export const mapCommitDeclaration = (
    raw: unknown,
): CommitDividendDeclarationResponse => {
    const wire = asRecord(raw);
    return {
        declaration: mapDeclaration(wire.declaration),
        // A commit's draft is a real, persisted entry — this is the one the
        // journal form opens. It is a DRAFT: the commit posts nothing.
        draft: mapJournalEntry(wire.draft),
        alreadyExisted: wire.alreadyExisted === true,
    };
};

// ── write edge ──────────────────────────────────────────────────────────────

/**
 * Tetri → the JSON **number** the write edge expects.
 *
 * The contract said decimal strings; this API takes decimals. The division is
 * exact for every amount this register can hold, and going out through the
 * integer means the value the user typed is never re-parsed from a float.
 * Sub-tetri input is refused server-side with `AMOUNT_PRECISION` rather than
 * rounded, so nothing silently loses a tetri.
 */
/**
 * Re-exported from `@/utils/money` (BOS-162): three mapping files each
 * defined their own copy of this, and callers import it from whichever one
 * they happened to be near.
 */
export { gelNumberOf };

// ── error codes ─────────────────────────────────────────────────────────────

/**
 * Every refusal this surface produces, from `DividendErrorCodes` in the API.
 *
 * Two vocabularies reach the client and both must be handled: these register
 * rules, and — when a dividends *write* fails a **journal** rule — the journal's
 * own codes instead (`ACCOUNT_NOT_FOUND` when the chart has no account tagged for
 * a dividends role, `AMOUNT_PRECISION` for sub-tetri money, `PERIOD_CLOSED`). A
 * handler that only knows this list shows a raw 400 for those.
 *
 * A malformed enum or date yields ASP.NET ProblemDetails — `{type, title, status,
 * errors{}, traceId}` with **no `code`** — so a message must also fall back to the
 * field-error envelope before it falls back to a generic failure.
 */
export const DIVIDEND_ERROR_CODES = [
    'SHAREHOLDER_NOT_FOUND',
    'SHAREHOLDER_HAS_DECLARATIONS',
    'NAME_REQUIRED',
    'IDENTIFIER_INVALID',
    'IDENTIFIER_DUPLICATE',
    'HOLDING_OUT_OF_RANGE',
    'RATE_OUT_OF_RANGE',
    'NOTHING_TO_DISTRIBUTE',
    'EXCEEDS_DISTRIBUTABLE_PROFIT',
    'NO_RATES_IN_FORCE',
    'DECLARATION_NOT_FOUND',
    'DATE_INVALID',
    'ALLOCATION_INVALID',
    'AMOUNT_NEGATIVE',
] as const;

export type DividendErrorCode = (typeof DIVIDEND_ERROR_CODES)[number];

export const isDividendErrorCode = (raw: unknown): raw is DividendErrorCode =>
    typeof raw === 'string' &&
    (DIVIDEND_ERROR_CODES as readonly string[]).includes(raw);
